import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Chip,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SearchIcon from '@mui/icons-material/Search';

/**
 * List Management Dialog Component
 * Comprehensive dialog for managing working lists
 *
 * @param {boolean} open - Whether dialog is open
 * @param {Function} onClose - Handler for closing dialog
 * @param {Array} parameterLists - All parameter lists
 * @param {Function} setParameterLists - Setter for parameter lists
 * @param {string} currentUser - Current user name
 * @param {Array} activeWorkingList - Currently active working list IDs
 * @param {Function} setActiveWorkingList - Setter for active working list
 * @param {Function} setSnackbar - Function to show snackbar feedback
 */
const ListManagementDialog = ({
  open,
  onClose,
  parameterLists,
  setParameterLists,
  currentUser,
  activeWorkingList,
  setActiveWorkingList,
  setSnackbar,
}) => {
  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Create list dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListPrivate, setNewListPrivate] = useState(true);

  // Rename dialog state
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renamingList, setRenamingList] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingList, setDeletingList] = useState(null);

  // Menu state (for list actions)
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedList, setSelectedList] = useState(null);

  // Filter lists by search
  const filteredLists = parameterLists?.filter(list =>
    list.name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Separate private and public lists
  const myPrivateLists = filteredLists.filter(l => l.isPrivate && l.createdBy === currentUser);
  const publicLists = filteredLists.filter(l => !l.isPrivate);

  // Handlers
  const handleOpenMenu = (event, list) => {
    setMenuAnchor(event.currentTarget);
    setSelectedList(list);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setSelectedList(null);
  };

  const handleCreateList = () => {
    if (!newListName.trim()) {
      setSnackbar({
        open: true,
        message: 'Bitte geben Sie einen Listennamen ein',
        severity: 'error'
      });
      return;
    }

    const newList = {
      id: Date.now(), // Simple ID generation
      name: newListName.trim(),
      isPrivate: newListPrivate,
      createdBy: currentUser,
      parameterIds: [],
      additionRequests: [],
      isDefault: false,
    };

    setParameterLists(prev => [...prev, newList]);
    setCreateDialogOpen(false);
    setNewListName('');
    setNewListPrivate(true);

    setSnackbar({
      open: true,
      message: `Liste "${newList.name}" wurde erstellt`,
      severity: 'success'
    });
  };

  const handleRenameList = () => {
    if (!renameValue.trim()) {
      setSnackbar({
        open: true,
        message: 'Bitte geben Sie einen Listennamen ein',
        severity: 'error'
      });
      return;
    }

    setParameterLists(prev =>
      prev.map(l => l.id === renamingList.id ? { ...l, name: renameValue.trim() } : l)
    );

    setSnackbar({
      open: true,
      message: `Liste wurde in "${renameValue.trim()}" umbenannt`,
      severity: 'success'
    });

    setRenameDialogOpen(false);
    setRenamingList(null);
    setRenameValue('');
  };

  const handleDeleteList = () => {
    setParameterLists(prev => prev.filter(l => l.id !== deletingList.id));

    // Remove from active working list if active
    if (activeWorkingList.includes(deletingList.id)) {
      setActiveWorkingList(prev => prev.filter(id => id !== deletingList.id));
    }

    setSnackbar({
      open: true,
      message: `Liste "${deletingList.name}" wurde gelöscht`,
      severity: 'success'
    });

    setDeleteDialogOpen(false);
    setDeletingList(null);
  };

  const handleDuplicateList = (list) => {
    const duplicatedList = {
      ...list,
      id: Date.now(),
      name: `${list.name} (Kopie)`,
      isPrivate: true,
      createdBy: currentUser,
      additionRequests: [], // Don't copy requests
    };

    setParameterLists(prev => [...prev, duplicatedList]);

    setSnackbar({
      open: true,
      message: `Liste "${duplicatedList.name}" wurde erstellt`,
      severity: 'success'
    });

    handleCloseMenu();
  };

  const handleSetAsActiveList = (listId) => {
    if (activeWorkingList.includes(listId)) {
      setSnackbar({
        open: true,
        message: 'Diese Liste ist bereits aktiv',
        severity: 'info'
      });
    } else if (activeWorkingList.length >= 2) {
      setSnackbar({
        open: true,
        message: 'Maximal 2 Arbeitslisten können gleichzeitig aktiv sein',
        severity: 'warning'
      });
    } else {
      setActiveWorkingList(prev => [...prev, listId]);
      const list = parameterLists.find(l => l.id === listId);
      setSnackbar({
        open: true,
        message: `"${list.name}" wurde als Arbeitsliste aktiviert`,
        severity: 'success'
      });
    }
    handleCloseMenu();
  };

  const handleOpenRename = (list) => {
    setRenamingList(list);
    setRenameValue(list.name);
    setRenameDialogOpen(true);
    handleCloseMenu();
  };

  const handleOpenDelete = (list) => {
    setDeletingList(list);
    setDeleteDialogOpen(true);
    handleCloseMenu();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PlaylistAddCheckIcon />
              <Typography variant="h6" fontWeight="bold">
                Listen verwalten
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Neue Liste
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Listen durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
            }}
            sx={{ mb: 3 }}
          />

          {/* Private Lists */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LockIcon fontSize="small" color="action" />
              <Typography variant="subtitle1" fontWeight="bold">
                Meine privaten Listen
              </Typography>
              <Chip label={myPrivateLists.length} size="small" />
            </Box>

            {myPrivateLists.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {myPrivateLists.map(list => (
                  <Card key={list.id} variant="outlined">
                    <CardContent sx={{ pb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {list.name}
                            </Typography>
                            {activeWorkingList.includes(list.id) && (
                              <Chip
                                label="Aktiv"
                                size="small"
                                color="primary"
                                icon={<CheckCircleIcon />}
                              />
                            )}
                            {list.isDefault && (
                              <Chip label="Standard" size="small" color="secondary" />
                            )}
                          </Box>
                          <Typography variant="caption" color="textSecondary">
                            {list.parameterIds.length} Parameter
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={(e) => handleOpenMenu(e, list)}>
                          <MoreVertIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                {searchTerm ? 'Keine Listen gefunden' : 'Noch keine privaten Listen vorhanden'}
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Public Lists */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <PublicIcon fontSize="small" color="action" />
              <Typography variant="subtitle1" fontWeight="bold">
                Öffentliche Listen
              </Typography>
              <Chip label={publicLists.length} size="small" />
            </Box>

            {publicLists.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {publicLists.map(list => {
                  const hasPendingRequests = list.additionRequests && list.additionRequests.length > 0;
                  return (
                    <Card key={list.id} variant="outlined">
                      <CardContent sx={{ pb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {list.name}
                              </Typography>
                              {activeWorkingList.includes(list.id) && (
                                <Chip
                                  label="Aktiv"
                                  size="small"
                                  color="primary"
                                  icon={<CheckCircleIcon />}
                                />
                              )}
                              {hasPendingRequests && (
                                <Badge badgeContent={list.additionRequests.length} color="warning">
                                  <Chip
                                    label="Vorschläge"
                                    size="small"
                                    color="warning"
                                    icon={<WarningAmberIcon />}
                                  />
                                </Badge>
                              )}
                            </Box>
                            <Typography variant="caption" color="textSecondary">
                              {list.parameterIds.length} Parameter • {list.createdBy}
                            </Typography>
                          </Box>
                          <IconButton size="small" onClick={(e) => handleOpenMenu(e, list)}>
                            <MoreVertIcon />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                Keine öffentlichen Listen verfügbar
              </Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
      >
        {selectedList && !activeWorkingList.includes(selectedList.id) && (
          <MenuItem onClick={() => handleSetAsActiveList(selectedList.id)}>
            <ListItemIcon>
              <PlaylistAddCheckIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Als Arbeitsliste aktivieren</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={() => handleDuplicateList(selectedList)}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplizieren</ListItemText>
        </MenuItem>

        {selectedList && selectedList.isPrivate && selectedList.createdBy === currentUser && (
          <>
            <Divider />
            <MenuItem onClick={() => handleOpenRename(selectedList)}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Umbenennen</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleOpenDelete(selectedList)}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Löschen</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Create List Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Neue Liste erstellen</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Listenname"
            value={newListName}
            onChange={(e) => setNewListName(e.target.value)}
            sx={{ mt: 2, mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label="Privat"
              icon={<LockIcon />}
              color={newListPrivate ? "primary" : "default"}
              onClick={() => setNewListPrivate(true)}
              sx={{ cursor: 'pointer' }}
            />
            <Chip
              label="Öffentlich"
              icon={<PublicIcon />}
              color={!newListPrivate ? "primary" : "default"}
              onClick={() => setNewListPrivate(false)}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleCreateList} variant="contained">Erstellen</Button>
        </DialogActions>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Liste umbenennen</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Neuer Name"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleRenameList} variant="contained">Umbenennen</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Liste löschen?</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie die Liste "{deletingList?.name}" wirklich löschen?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Diese Aktion kann nicht rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleDeleteList} variant="contained" color="error">Löschen</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ListManagementDialog;
